import { redirect } from 'react-router-dom';

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import { KeyedMutator, default as useSWR } from 'swr';

import { get as lodashGet } from 'lodash';

import { removeToken } from 'utils/storage';

import { BASE_URL, URLS } from 'config/urls';

export type SwrMutator = KeyedMutator<any>;

export { BASE_URL };

export type IConfig<D> = AxiosRequestConfig<D> & {
  isFileUpload?: boolean;
  useSharableToken?: boolean;
};

export const getConfig = <D>(params?: IConfig<D>) => {
  const { headers = {}, isFileUpload = false, ...options } = params || {};
  const contentType = isFileUpload ? 'multipart/form-data' : 'application/json';
  const config: IConfig<D> = {
    headers: {
      'Content-Type': contentType,
      ...headers
    } as IConfig<D>['headers'],
    withCredentials: true,
    ...options
  };
  return config;
};

const extractData = <R, D>(response: AxiosResponse<R, D>) => response.data;

const handleErrorResponse = (error: any) => {
  if (error.response?.status === 401) {
    removeToken();
    redirect(URLS.LOGIN);
  }

  if (error.response?.status === 400 && error.response?.data.errors) {
    const formErrors = Object.values(
      error.response.data.errors as Record<
        string,
        { field: string; message: string }
      >
    ).map(({ field, message }) => {
      if (field) {
        return {
          fieldName: field,
          message: message
        };
      }
      return {
        fieldName: 'root.serverError',
        message: message
      };
    });

    const setFormErrors = (
      setError: (
        name: string,
        error: { type: 'custom'; message: string }
      ) => void
    ) => {
      formErrors?.map(
        ({ fieldName, message }: { fieldName: string; message: string }) => {
          setError(fieldName as any, {
            type: 'custom',
            message: message
          });
        }
      );
    };
    return Promise.reject({
      ...error.response.data,
      setFormErrors,
      formErrors
    });
  }

  return Promise.reject(error?.response?.data || 'Something went wrong');
};

export const get = <R, D = any>(url: string, params?: IConfig<D>): Promise<R> =>
  axios
    .get<R>(url, getConfig(params))
    .then(extractData)
    .catch(handleErrorResponse);

export const post = <R, D = any>(
  url: string,
  data?: D,
  params?: IConfig<D>
): Promise<R> =>
  axios
    .post<R>(url, data, getConfig(params))
    .then(extractData)
    .catch(handleErrorResponse);

export const useFetch = <D>(
  url: string | undefined,
  queryParams?: Record<string, any>
) => {
  const queryString = queryParams
    ? `?${qs.stringify(queryParams, { arrayFormat: 'repeat' })}`
    : '';
  const fullUrl = url ? `${BASE_URL}${url}${queryString}` : undefined;
  return useSWR<D>(fullUrl, get, {
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true
  });
};

export interface SortRequestProps {
  sort?: string;
}

export interface ListSortProps {
  sort?: string;
  direction: string;
}

export const buildSort = (sort: ListSortProps) => ({
  sort: `${sort.direction === 'desc' ? '-' : ''}${sort.sort}`
});

export const requestSdk = async (sdk: any) => {
  let result: any = { data: {}, errors: [], success: false };

  try {
    const response = await sdk();

    result.data = response.data;
    result.success = true;
  } catch (error: any) {
    if (!error.response) {
      result.errors = [{ message: error.toString(), status: null }];
      return result;
    }

    if (error.response.status === 404) {
      result.errors = [{ message: 'Not found', status: error.response.status }];
    } else {
      result.errors = lodashGet(error, 'response.data.errors', [
        'Unknown error.'
      ]);
    }
  }

  return result;
};